import { useMemo, useState } from "react";
import MetricBreakdownCard from "@/components/dashboard/MetricBreakdownCard";
import RevenueExpensesChart from "@/components/dashboard/charts/RevenueExpensesChart";
import ProjectPerformanceChart from "@/components/dashboard/charts/ProjectPerformanceChart";
import ForecastSection from "@/components/dashboard/ForecastSection";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import DashboardRangeNotice from "@/components/dashboard/DashboardRangeNotice";
import useDashboardData from "@/hooks/useDashboardData";
import { Card, CardContent } from "@/components/ui/card";
import { DASHBOARD_SPARKLINE_COLORS } from "@/lib/dashboardColors";

export default function DashboardV2() {
  const [forecastScenario, setForecastScenario] = useState("neutral");
  const {
    overview,
    overviewLoading,
    overviewError,
    chartData,
    chartsLoading,
    chartsError,
    revenueSeries,
    expenseSeries,
    netSeries,
    forecast,
    forecastTax,
    forecastLoading,
    forecastError,
    topProjects,
    rangeMode,
    forecastStartLabel,
    comparisonLabel,
    comparisonRangeLabel,
    forecastMonthsAhead,
    rangeStart,
    rangeEnd,
  } = useDashboardData({
    includeStatus: false,
    forecastScenario,
  });

  const showHistorical = rangeMode !== "future";
  const showForecast = rangeMode === "mixed";

  const breakdownMetrics = useMemo(() => {
    if (!overview) return null;

    const revenueNetTotal = (revenueSeries || []).reduce(
      (sum, point) => sum + (point.net ?? point.value ?? 0),
      0
    );
    const revenueVatTotal = (revenueSeries || []).reduce(
      (sum, point) => sum + (point.vat ?? 0),
      0
    );
    const revenueGrossTotal = revenueNetTotal + revenueVatTotal;

    // Expected (open/pending) invoices in the period
    const openInvoicesTotal = overview.open_invoices?.total || 0;

    const expenseGrossTotal = (expenseSeries || []).reduce(
      (sum, point) => sum + (point.value ?? 0),
      0
    );
    const expenseVatTotal = (expenseSeries || []).reduce(
      (sum, point) => sum + (point.vat ?? 0),
      0
    );
    const expenseNetTotal = (expenseSeries || []).reduce(
      (sum, point) => sum + (point.net ?? point.value ?? 0),
      0
    );

    const totalRevenueGross = revenueGrossTotal + openInvoicesTotal;
    const profitGross = totalRevenueGross - expenseGrossTotal;
    const profitTax = revenueVatTotal - expenseVatTotal;
    const profitNet = revenueNetTotal + openInvoicesTotal - expenseNetTotal;

    return {
      revenue: {
        gross: revenueGrossTotal || overview.revenue?.current || 0,
        open: openInvoicesTotal,
        total: totalRevenueGross,
        tax: revenueVatTotal,
        net: revenueNetTotal,
      },
      expenses: {
        gross: expenseGrossTotal || overview.expenses?.current || 0,
        tax: expenseVatTotal,
        net: expenseNetTotal,
      },
      profit: {
        gross: profitGross,
        tax: profitTax,
        net: profitNet || overview.net?.current || 0,
      },
    };
  }, [overview, revenueSeries, expenseSeries]);

  const revenueSparkline = useMemo(() => {
    return (revenueSeries || [])
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((point) => ({ value: point.net ?? point.value ?? 0 }));
  }, [revenueSeries]);
  const expenseSparkline = useMemo(() => {
    return (expenseSeries || [])
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((point) => ({ value: point.net ?? point.value ?? 0 }));
  }, [expenseSeries]);
  const profitSparkline = useMemo(() => {
    return (netSeries || [])
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((point) => ({ value: point.value ?? 0 }));
  }, [netSeries]);

  return (
    <div className="space-y-6" data-testid="dashboard-finance-view">
      <DashboardFilters
        title="Finanz-Filter"
        subtitle="Fokussiere Umsätze, Ausgaben und Cashflow"
        showStatus={false}
      />

      <DashboardRangeNotice mode={rangeMode} forecastStartLabel={forecastStartLabel} />

      <section data-testid="dashboard-kpi-grid">
        {!showHistorical && (
          <Card className="col-span-full">
            <CardContent className="p-6 text-sm text-slate-600" data-testid="dashboard-no-data">
              Für reine Zukunftszeiträume stehen keine historischen Kennzahlen zur Verfügung.
            </CardContent>
          </Card>
        )}
        {overviewLoading && (
          <Card className="col-span-full">
            <CardContent className="p-6 text-sm text-slate-600" data-testid="kpi-loading">
              KPI-Daten werden geladen...
            </CardContent>
          </Card>
        )}

        {!overviewLoading && overviewError && (
          <Card className="col-span-full">
            <CardContent className="p-6 text-sm text-red-600" data-testid="kpi-error">
              KPI-Daten konnten nicht geladen werden.
            </CardContent>
          </Card>
        )}

        {!overviewLoading && !overviewError && !breakdownMetrics && (
          <Card className="col-span-full">
            <CardContent className="p-6 text-sm text-gray-500" data-testid="kpi-empty">
              Keine KPI-Daten verfügbar.
            </CardContent>
          </Card>
        )}

        {showHistorical && !overviewLoading && !overviewError && breakdownMetrics && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <MetricBreakdownCard
              title="Einnahmen"
              testId="finance-card-revenue"
              changePercent={overview?.revenue?.change_percent}
              comparisonLabel={comparisonLabel}
              comparisonRangeLabel={comparisonRangeLabel}
              comparisonValue={overview?.revenue?.previous}
              sparklineData={revenueSparkline}
              sparklineColor={DASHBOARD_SPARKLINE_COLORS.revenue}
              showComparison={false}
              items={[
                { label: "Realisiert", value: breakdownMetrics.revenue.gross, testId: "finance-revenue-gross" },
                { label: "Offen (Rechnungen)", value: breakdownMetrics.revenue.open, testId: "finance-revenue-open" },
                { label: "Gesamt", value: breakdownMetrics.revenue.total, testId: "finance-revenue-total" },
              ]}
            />
            <MetricBreakdownCard
              title="Ausgaben"
              testId="finance-card-expenses"
              changePercent={overview?.expenses?.change_percent}
              comparisonLabel={comparisonLabel}
              comparisonRangeLabel={comparisonRangeLabel}
              comparisonValue={overview?.expenses?.previous}
              sparklineData={expenseSparkline}
              sparklineColor={DASHBOARD_SPARKLINE_COLORS.expenses}
              invertTrend
              showComparison={false}
              items={[
                { label: "Brutto", value: breakdownMetrics.expenses.gross, testId: "finance-expenses-gross" },
                { label: "Steuern", value: breakdownMetrics.expenses.tax, testId: "finance-expenses-tax" },
                { label: "Netto", value: breakdownMetrics.expenses.net, testId: "finance-expenses-net" },
              ]}
            />
            <MetricBreakdownCard
              title="Gewinn"
              testId="finance-card-profit"
              changePercent={overview?.net?.change_percent}
              comparisonLabel={comparisonLabel}
              comparisonRangeLabel={comparisonRangeLabel}
              comparisonValue={overview?.net?.previous}
              sparklineData={profitSparkline}
              sparklineColor={DASHBOARD_SPARKLINE_COLORS.profit}
              showComparison={false}
              items={[
                { label: "Realisiert", value: breakdownMetrics.revenue.gross - breakdownMetrics.expenses.gross, testId: "finance-profit-gross" },
                { label: "Inkl. Offene", value: breakdownMetrics.profit.gross, testId: "finance-profit-total" },
                { label: "Netto", value: breakdownMetrics.profit.net, testId: "finance-profit-net" },
              ]}
            />
          </div>
        )}
      </section>

      {showHistorical && (
        <section data-testid="dashboard-chart-grid">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RevenueExpensesChart data={chartData} isLoading={chartsLoading} error={chartsError} />
            <ProjectPerformanceChart data={topProjects} isLoading={overviewLoading} error={overviewError} />
          </div>
        </section>
      )}

      {showForecast && (
        <section data-testid="dashboard-forecast-section">
          <ForecastSection
            historicalRevenue={revenueSeries}
            historicalExpenses={expenseSeries}
            forecastData={forecast}
            taxEstimate={forecastTax}
            isLoading={forecastLoading}
            error={forecastError}
            scenario={forecastScenario}
            onScenarioChange={setForecastScenario}
            forecastMonths={forecastMonthsAhead}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />
        </section>
      )}
    </div>
  );
}